// src/components/GalleryPage.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { galleryService } from '../services/galleryService';
import './GalleryPage.css';

const GalleryPage = () => {
    const [activeTab, setActiveTab] = useState('photos');
    const [media, setMedia] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [loading, setLoading] = useState(true);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        loadMedia();
        loadCategories();
    }, [activeTab, selectedCategory]);

    const loadMedia = async () => {
        setLoading(true);
        const type = activeTab === 'photos' ? 'image' : 'video';
        const response = await galleryService.getMedia(type, selectedCategory !== 'all' ? selectedCategory : '', 100);
        if (response.success) {
            setMedia(response.media);
        }
        setLoading(false);
    };

    const loadCategories = async () => {
        const response = await galleryService.getCategories();
        if (response.success) {
            setCategories(response.categories);
        }
    };

    const handleMediaClick = async (item) => {
        setSelectedMedia(item);
        setShowModal(true);
        await galleryService.incrementViews(item.id);
    };

    const getFullImageUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        if (url.startsWith('/')) return `https://nlngraclub.com${url}`;
        return `https://nlngraclub.com/${url}`;
    };

    return (
        <div className="gallery-page">
            {/* Hero Section */}
            <div className="page-hero">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <span className="eyebrow">Our Memories</span>
                        <h1>Gallery & Videos</h1>
                        <p>Explore moments captured at NLNG RA Club</p>
                    </motion.div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="gallery-tabs">
                <div className="container">
                    <button 
                        className={`tab-btn ${activeTab === 'photos' ? 'active' : ''}`}
                        onClick={() => setActiveTab('photos')}
                    >
                        <i className="fas fa-images"></i>
                        Photo Gallery
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'videos' ? 'active' : ''}`}
                        onClick={() => setActiveTab('videos')}
                    >
                        <i className="fas fa-video"></i>
                        Video Gallery
                    </button>
                </div>
            </div>

            {/* Category Filter */}
            {categories.length > 0 && (
                <div className="gallery-categories">
                    <div className="container">
                        <div className="category-filter">
                            <button 
                                className={`category-chip ${selectedCategory === 'all' ? 'active' : ''}`}
                                onClick={() => setSelectedCategory('all')}
                            >
                                All
                            </button>
                            {categories.map(cat => (
                                <button 
                                    key={cat.id}
                                    className={`category-chip ${selectedCategory === cat.name ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(cat.name)}
                                >
                                    <i className={`fas ${cat.icon || 'fa-tag'}`}></i>
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Media Grid */}
            <section className="gallery-section">
                <div className="container">
                    {loading ? (
                        <div className="loading-container">
                            <div className="spinner"></div>
                            <p>Loading {activeTab}...</p>
                        </div>
                    ) : media.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-camera"></i>
                            <h3>No {activeTab} found</h3>
                            <p>Check back later for new content</p>
                        </div>
                    ) : (
                        <div className="gallery-grid">
                            {media.map((item, index) => (
                                <motion.div
                                    key={item.id}
                                    className="gallery-card"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    whileHover={{ y: -5 }}
                                    onClick={() => handleMediaClick(item)}
                                >
                                    <div className="gallery-card-image">
                                        {item.media_type === 'image' ? (
                                            <img 
                                                src={getFullImageUrl(item.thumbnail_url || item.file_url)} 
                                                alt={item.title}
                                                loading="lazy"
                                                onError={(e) => {
                                                    e.target.src = 'https://nlngraclub.com/images/company/company-image.jpg';
                                                }}
                                            />
                                        ) : (
                                            <div className="video-thumbnail">
                                                <img 
                                                    src={getFullImageUrl(item.thumbnail_url || 'https://nlngraclub.com/images/video-placeholder.jpg')} 
                                                    alt={item.title}
                                                    onError={(e) => {
                                                        e.target.src = 'https://nlngraclub.com/images/video-placeholder.jpg';
                                                    }}
                                                />
                                                <div className="play-icon">
                                                    <i className="fas fa-play"></i>
                                                </div>
                                            </div>
                                        )}
                                        {item.featured === 1 && (
                                            <span className="featured-badge">
                                                <i className="fas fa-star"></i> Featured
                                            </span>
                                        )}
                                    </div>
                                    <div className="gallery-card-info">
                                        <h3>{item.title}</h3>
                                        {item.description && <p>{item.description.substring(0, 80)}...</p>}
                                        <div className="card-meta">
                                            <span><i className="fas fa-eye"></i> {item.views || 0}</span>
                                            {item.category && <span><i className="fas fa-tag"></i> {item.category}</span>}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Lightbox Modal */}
            <AnimatePresence>
                {showModal && selectedMedia && (
                    <motion.div 
                        className="gallery-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowModal(false)}
                    >
                        <motion.div 
                            className="gallery-modal-content"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                            
                            {selectedMedia.media_type === 'image' ? (
                                <img 
                                    src={getFullImageUrl(selectedMedia.file_url)} 
                                    alt={selectedMedia.title}
                                    className="modal-image"
                                />
                            ) : (
                                <video 
                                    src={getFullImageUrl(selectedMedia.file_url)} 
                                    controls 
                                    autoPlay
                                    className="modal-video"
                                >
                                    Your browser does not support the video tag.
                                </video>
                            )}
                            
                            <div className="modal-info">
                                <h2>{selectedMedia.title}</h2>
                                <p>{selectedMedia.description}</p>
                                <div className="modal-meta">
                                    <span><i className="fas fa-calendar"></i> {new Date(selectedMedia.created_at).toLocaleDateString()}</span>
                                    <span><i className="fas fa-eye"></i> {selectedMedia.views} views</span>
                                    {selectedMedia.category && <span><i className="fas fa-tag"></i> {selectedMedia.category}</span>}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default GalleryPage;